import React, { useState, useEffect } from 'react';
import { FiBarChart2 } from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { fetchRuns } from '../services/api';

const ResourceUtilization = () => {
  const [data, setData] = useState([]);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSystemMetrics = async () => {
      try {
        const runsResponse = await fetchRuns();
        // Handle both cases: when fetchRuns returns an array directly or an object with runs property
        const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);
        
        if (runs.length === 0) {
          setData([]);
          return;
        }

        // Get the latest run with system metrics
        const latestRunWithMetrics = runs.find(run => 
          run.system_metrics && typeof run.system_metrics === 'object'
        );

        if (!latestRunWithMetrics || !latestRunWithMetrics.system_metrics) {
          // No real metrics available
          setData([]);
          return;
        }

        // Extract metrics from the latest run (now it's an object, not an array)
        const metrics = latestRunWithMetrics.system_metrics;
        
        // Calculate disk usage percentage
        const diskTotal = metrics.disk_total_space_bytes || 1;
        const diskUsed = metrics.disk_used_space_bytes || 0;
        const diskUsagePercent = Math.round((diskUsed / diskTotal) * 100);
        
        // Calculate memory usage percentage
        const memoryUsagePercent = Math.round((metrics['otel::system.memory.utilization'] || 0) * 100);
        
        // Calculate process memory usage percentage
        const processMemoryInUse = metrics.process_memory_in_use || metrics['otel::process.memory.usage'] || 0;
        const processMemoryAvailable = metrics.process_memory_available || 0;
        
        // Try different calculation methods for process memory
        let processMemoryPercent = 0;
        
        // Method 1: If we have both in_use and available
        if (processMemoryInUse > 0 && processMemoryAvailable > 0) {
          processMemoryPercent = Math.round((processMemoryInUse / (processMemoryInUse + processMemoryAvailable)) * 100);
        }
        // Method 2: If we only have in_use, assume it's a percentage of system memory
        else if (processMemoryInUse > 0 && metrics['otel::system.memory.usage'] > 0) {
          const systemMemoryUsage = metrics['otel::system.memory.usage'];
          processMemoryPercent = Math.round((processMemoryInUse / systemMemoryUsage) * 100);
        }
        // Method 3: Check if there's a direct utilization metric
        else if (metrics['otel::process.memory.utilization']) {
          processMemoryPercent = Math.round(metrics['otel::process.memory.utilization'] * 100);
        }
        
        // Log metrics for debugging
        console.log('Process Memory Metrics:', {
          processMemoryInUse,
          processMemoryAvailable,
          processMemoryPercent,
          allMetrics: metrics
        });
        
        // Only include metrics that have valid data (non-zero values)
        const processedData = [];
        
        // CPU metric - only show if there's actual data
        const cpuUsage = Math.round((metrics['otel::system.cpu.utilization'] || 0) * 100);
        if (cpuUsage > 0) {
          processedData.push({ name: 'CPU', usage: cpuUsage });
        }
        
        // Memory metric - only show if there's actual data
        if (memoryUsagePercent > 0) {
          processedData.push({ name: 'Memory', usage: memoryUsagePercent });
        }
        
        // Storage metric - only show if there's actual data
        if (diskUsagePercent > 0) {
          processedData.push({ name: 'Storage', usage: diskUsagePercent });
        }
        
        // Process CPU metric - only show if there's actual data
        const processCpuUsage = Math.round((metrics['otel::process.cpu.utilization'] || metrics.process_cpu_utilization_percent || 0) * 100);
        if (processCpuUsage > 0) {
          processedData.push({ name: 'Process CPU', usage: processCpuUsage });
        }
        
        // Process Memory metric - show if we have any process memory data
        if (processMemoryInUse > 0) {
          // If we couldn't calculate a percentage, show a minimum bar
          const displayPercent = processMemoryPercent > 0 ? processMemoryPercent : 15;
          // Ensure minimum 15% visibility for Process Memory so it's clearly visible
          const displayUsage = Math.max(displayPercent, 15);
          processedData.push({ 
            name: 'Process Memory',
            usage: displayUsage,
            actualUsage: processMemoryPercent,
            actualMB: (processMemoryInUse / 1024 / 1024).toFixed(2),
            hasPercentage: processMemoryPercent > 0
          });
          }

        setData(processedData);
        setLatestRun(latestRunWithMetrics);
      } catch (error) {
        console.error('Error loading system metrics:', error);
        // Don't show any data on error
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadSystemMetrics();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const getDetailedInfo = (label, value, data) => {
        if (!latestRun || !latestRun.system_metrics) return null;
        
        const metrics = latestRun.system_metrics;
        switch(label) {
          case 'CPU':
            return `System: ${value}%`;
          case 'Memory':
            const memUsed = (metrics['otel::system.memory.usage'] / 1024 / 1024 / 1024).toFixed(2);
            return `${value}% (${memUsed} GB used)`;
          case 'Storage':
            const diskUsed = (metrics.disk_used_space_bytes / 1024 / 1024 / 1024).toFixed(2);
            const diskTotal = (metrics.disk_total_space_bytes / 1024 / 1024 / 1024).toFixed(2);
            return `${value}% (${diskUsed} GB / ${diskTotal} GB)`;
          case 'Process CPU':
            return `Process: ${value}%`;
          case 'Process Memory':
            // If we have actual usage data, show it
            if (data && data.actualMB) {
              if (data.hasPercentage) {
                const actualPercent = data.actualUsage !== undefined ? data.actualUsage : value;
                return `${data.actualMB} MB (${actualPercent}%)`;
              } else {
                return `${data.actualMB} MB`;
              }
            }
            const procMem = (metrics.process_memory_in_use / 1024 / 1024).toFixed(2);
            return `${procMem} MB`;
          default:
            return `${value}%`;
        }
      };
      
      const itemData = payload[0].payload;
      const metricColor = getBarColor(label, payload[0].value);
      
      return (
        <div className="bg-white p-3 shadow-md rounded-md border border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: metricColor }} />
          <p className="font-medium text-sm font-inter">{label}</p>
          </div>
          <p className="text-xs text-gray-700 font-inter">
            {getDetailedInfo(label, payload[0].value, itemData) || `${payload[0].value}% utilization`}
          </p>
        </div>
      );
    }
    return null;
  };

  const getBarColor = (name, usage) => {
    // Different colors for each metric type
    const metricColors = {
      'CPU': {
        low: '#3B82F6',      // Blue
        medium: '#2563EB',   // Darker Blue
        high: '#1D4ED8'      // Even Darker Blue
      },
      'Memory': {
        low: '#10B981',      // Green
        medium: '#059669',   // Darker Green
        high: '#047857'      // Even Darker Green
      },
      'Storage': {
        low: '#8B5CF6',      // Purple
        medium: '#7C3AED',   // Darker Purple
        high: '#6D28D9'      // Even Darker Purple
      },
      'Process CPU': {
        low: '#F59E0B',      // Amber
        medium: '#D97706',   // Darker Amber
        high: '#B45309'      // Even Darker Amber
      },
      'Process Memory': {
        low: '#EC4899',      // Pink
        medium: '#DB2777',   // Darker Pink
        high: '#BE185D'      // Even Darker Pink
      },
      'Network': {
        low: '#14B8A6',      // Teal
        medium: '#0D9488',   // Darker Teal
        high: '#0F766E'      // Even Darker Teal
      },
      'GPU': {
        low: '#EF4444',      // Red
        medium: '#DC2626',   // Darker Red
        high: '#B91C1C'      // Even Darker Red
      }
    };

    const colors = metricColors[name] || metricColors['CPU'];
    
    if (usage > 80) return colors.high;
    if (usage > 60) return colors.medium;
    return colors.low;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow-md w-full h-full">
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h2 className="text-[18px] font-semibold text-[#1F2937] font-inter">Resource Utilization</h2>
              <p className="text-sm text-[#6B7280] font-inter">System Performance Metrics</p>
            </div>
          </div>
          <div className="flex-grow flex items-center justify-center">
        <div className="text-gray-500">Loading system metrics...</div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no resource data
  if (!loading && data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiBarChart2 className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Resource Data Available</h3>
          <p className="text-gray-500">Resource utilization will appear here once system metrics are available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md w-full h-full">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-[18px] font-semibold text-[#1F2937] font-inter">Resource Utilization</h2>
            <p className="text-sm text-[#6B7280] font-inter">System Performance Metrics</p>
          </div>
        </div>
        
        <div className="flex-grow mt-3">
          <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
            <XAxis 
              dataKey="name" 
              axisLine={false} 
              tickLine={false}
              tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
            />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="usage" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.name, entry.usage)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ResourceUtilization;